/**
 * AdminThemeContext - Gestion du thème clair/sombre pour l'interface admin
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminThemeContext = createContext();

export function AdminThemeProvider({ children }) {
  // État initial : 'dark' par défaut (thème sombre)
  const [theme, setTheme] = useState('dark');
  const [isInitialized, setIsInitialized] = useState(false);

  // Lire le localStorage après le montage (si aucune préférence, garder 'dark')
  useEffect(() => {
    const saved = localStorage.getItem('admin-theme');
    if (saved && (saved === 'light' || saved === 'dark')) {
      setTheme(saved);
    } else {
      // Pas de préférence sauvegardée, forcer le thème sombre par défaut
      localStorage.setItem('admin-theme', 'dark');
    }
    setIsInitialized(true);
  }, []);

  // Appliquer les classes CSS au document et sauvegarder dans localStorage
  useEffect(() => {
    if (isInitialized) {
      const root = document.documentElement;
      localStorage.setItem('admin-theme', theme);
      
      // Appliquer les classes sur <html> pour les styles CSS globaux
      if (theme === 'dark') {
        root.classList.remove('light');
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
        root.classList.add('light');
      }
    }
  }, [theme, isInitialized]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isLight: theme === 'light',
    isDark: theme === 'dark'
  };

  return (
    <AdminThemeContext.Provider value={value}>
      {children}
    </AdminThemeContext.Provider>
  );
}

export function useAdminTheme() {
  const context = useContext(AdminThemeContext);
  if (!context) {
    throw new Error('useAdminTheme must be used within AdminThemeProvider');
  }
  return context;
}

// Classes CSS pour les thèmes
export const adminThemeClasses = {
  light: {
    // Backgrounds - Style corporate moderne
    bg: 'bg-slate-50',
    bgSecondary: 'bg-white',
    bgCard: 'bg-white',
    bgSidebar: 'bg-slate-900',  // Sidebar sombre même en mode light
    bgHeader: 'bg-white',
    bgHover: 'hover:bg-slate-100',
    bgActive: 'bg-indigo-50',
    bgInput: 'bg-white',
    
    // Borders
    border: 'border-slate-200',
    borderInput: 'border-slate-300',
    
    // Text
    text: 'text-slate-900',
    textSecondary: 'text-slate-600',
    textMuted: 'text-slate-500',
    textHeading: 'text-slate-900',
    
    // Buttons - Style corporate avec indigo
    btnPrimary: 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm',
    btnSecondary: 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm',
    btnOutline: 'border-slate-300 text-slate-700 hover:bg-slate-50',
    btnGhost: 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
    btnDanger: 'bg-red-600 hover:bg-red-700 text-white shadow-sm',
    btnSuccess: 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm',
    
    // Cards & Panels
    card: 'bg-white border border-slate-200 shadow-sm rounded-xl',
    panel: 'bg-slate-50 border border-slate-200 rounded-lg',
    
    // Menu items (sidebar sombre)
    menuItem: 'text-slate-300 hover:bg-slate-800 hover:text-white',
    menuItemActive: 'bg-indigo-600 text-white font-medium',
    
    // Status colors
    success: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    warning: 'bg-amber-50 text-amber-700 border border-amber-200',
    error: 'bg-red-50 text-red-700 border border-red-200',
    info: 'bg-blue-50 text-blue-700 border border-blue-200',
    
    // Badges
    badge: 'bg-slate-100 text-slate-700',
    badgePrimary: 'bg-indigo-100 text-indigo-700',
    badgeSuccess: 'bg-emerald-100 text-emerald-700',
    badgeWarning: 'bg-amber-100 text-amber-700',
    badgeDanger: 'bg-red-100 text-red-700',
    
    // Table
    tableHeader: 'bg-slate-50 text-slate-700',
    tableRow: 'hover:bg-slate-50',
    tableRowAlt: 'bg-slate-50/50',
  },
  dark: {
    // Backgrounds
    bg: 'bg-slate-950',
    bgSecondary: 'bg-slate-900',
    bgCard: 'bg-slate-900',
    bgSidebar: 'bg-slate-950',
    bgHeader: 'bg-slate-900/80',
    bgHover: 'hover:bg-slate-800',
    bgActive: 'bg-slate-800',
    bgInput: 'bg-slate-800',
    
    // Borders
    border: 'border-slate-800',
    borderInput: 'border-slate-700',
    
    // Text
    text: 'text-white',
    textSecondary: 'text-slate-300',
    textMuted: 'text-slate-500',
    textHeading: 'text-white',
    
    // Buttons
    btnPrimary: 'bg-indigo-600 hover:bg-indigo-500 text-white',
    btnSecondary: 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700',
    btnOutline: 'border-slate-700 text-slate-300 hover:bg-slate-800',
    btnGhost: 'text-slate-400 hover:bg-slate-800 hover:text-white',
    btnDanger: 'bg-red-600 hover:bg-red-500 text-white',
    btnSuccess: 'bg-emerald-600 hover:bg-emerald-500 text-white',
    
    // Cards & Panels
    card: 'bg-slate-900 border border-slate-800 rounded-xl',
    panel: 'bg-slate-800/50 border border-slate-700 rounded-lg',
    
    // Menu items
    menuItem: 'text-slate-400 hover:bg-slate-800 hover:text-white',
    menuItemActive: 'bg-indigo-600 text-white',
    
    // Status colors
    success: 'bg-emerald-500/20 text-emerald-400',
    warning: 'bg-amber-500/20 text-amber-400',
    error: 'bg-red-500/20 text-red-400',
    info: 'bg-blue-500/20 text-blue-400',
    
    // Badges
    badge: 'bg-slate-800 text-slate-300',
    badgePrimary: 'bg-indigo-500/20 text-indigo-400',
    badgeSuccess: 'bg-emerald-500/20 text-emerald-400',
    badgeWarning: 'bg-amber-500/20 text-amber-400',
    badgeDanger: 'bg-red-500/20 text-red-400',
    
    // Table
    tableHeader: 'bg-slate-800/50 text-slate-300',
    tableRow: 'hover:bg-slate-800/50',
    tableRowAlt: 'bg-slate-800/30',
  }
};

export default AdminThemeContext;
